import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {
  computeAnchorSelectedEvaluatedCells,
  computeHasLockedCellInSelection,
  computeIsCellSelectionEditingLocked,
  computeIsTableLocked,
  computeLockedWarningMessage,
  computeSelectedEvaluatedCells,
} from 'notebook/tables/store/logic/TableLockingLogic';
import {
  createDeepSelector,
  createTableStoreSelector,
} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore, TableEditableVolatileProps} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';
import {
  getLockedCellWarningMessage,
  getLockedTableWarningMessage,
  LockedCellWarningType,
  LockedTableWarningType,
} from 'notebook/tables/util/LockedTableWarningMessages';

import {TableSelectionModel} from './TableSelectionSelectors';

/**
 * Model containing properties related to table locking and permissions about the editability of the table.
 */
export interface TableLockingModel {
  /**
   * Whether the table is in a locked state.
   * A table is locked if the locked prop is true OR if it has placeholder rows.
   */
  isTableLocked: boolean;

  /**
   * Whether editing is locked for the current cell selection (full selection, not anchor-only).
   */
  isCellSelectionLocked: boolean;

  /**
   * Whether editing is locked for the current cell selection.
   * Same as isCellSelectionLocked; used by isLockedWithEditWarn which may show a notification.
   */
  isLockedWithEditWarn: boolean;

  /**
   * Warning message when editing is locked for the current selection, or null.
   */
  lockedWarningMessage: string | null;

  /**
   * Whether editing is locked when considering only the anchor cell (and its merged range).
   */
  isAnchorCellSelectionLocked: boolean;

  /**
   * Whether editing is locked for undo/redo (allows actions involving locked cells).
   */
  isCellSelectionLockedForUndoRedo: boolean;

  /**
   * Whether any cell in the current selection is locked on the table itself, which disables cutting
   * the selection.
   */
  hasLockedCellInSelection: boolean;

  /**
   * Warning message to show when the table locked prop is true (e.g. table context menu).
   */
  editWhileTableLockedWarningMessage: string | null;

  /**
   * Warning message when the anchor cell selection is locked, using the expanded selection for cell warnings.
   */
  lockedWarningMessageIfAnchorLocked: string | null;

  /**
   * Warning message for undo/redo locking checks.
   */
  lockedWarningMessageForUndoRedo: string | null;

  /**
   * Indices of the rows that are locked against deletion (finalized and not archived). The array
   * identity is stable while its contents are unchanged.
   */
  lockedRowIndices: Array<number>;
}

export function resolveEditWhileTableLockedWarning(
  editWhileTableLockedWarning: string | null | undefined
): string | null {
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  if (editWhileTableLockedWarning === null) {
    return null;
  }
  return editWhileTableLockedWarning ?? getLockedTableWarningMessage(LockedTableWarningType.GENERIC);
}

export function resolveGetEditWhileCellLockedWarning(
  getEditWhileCellLockedWarning: ((selection: MultiSelectionBox) => string) | null | undefined
): ((selection: MultiSelectionBox) => string) | null {
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  if (getEditWhileCellLockedWarning === null) {
    return null;
  }
  return getEditWhileCellLockedWarning ?? (() => getLockedCellWarningMessage(LockedCellWarningType.GENERIC));
}

function createLockedRowIndicesSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps
): (store: TableEditableStore) => Array<number> {
  return createDeepSelector.withTypes<TableEditableStore>()(
    [(store) => volatilePropsSelector(store).table],
    (table) => TableUtil.lockedRowIndices(table) ?? []
  );
}

/**
 * Creates a memoized selector that computes the TableLockingModel from the store.
 */
export function createTableLockingSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps,
  tableSelectionSelector: (store: TableEditableStore) => TableSelectionModel
): (store: TableEditableStore) => TableLockingModel {
  return createTableStoreSelector(
    'tableLocking',
    {
      locked: (store) => store.stableProps.locked ?? false,
      hasPlaceholderRows: (store) => store.stableProps.hasPlaceholderRows ?? false,
      selection: tableSelectionSelector,
      computedTable: (store) => volatilePropsSelector(store).computedTable,
      table: (store) => volatilePropsSelector(store).table,
      editWhileTableLockedWarningProp: (store) => store.stableProps.editWhileTableLockedWarning,
      getEditWhileCellLockedWarningProp: (store) => store.stableProps.getEditWhileCellLockedWarning,
      multiStartIndex: (store) => store.state.multiStartIndex,
      lockedRowIndices: createLockedRowIndicesSelector(volatilePropsSelector),
    },
    ({
      locked,
      hasPlaceholderRows,
      selection,
      computedTable,
      table,
      editWhileTableLockedWarningProp,
      getEditWhileCellLockedWarningProp,
      multiStartIndex,
      lockedRowIndices,
    }) => {
      const isTableLocked = computeIsTableLocked(locked, hasPlaceholderRows);
      const selectedCells = computeSelectedEvaluatedCells({
        hasMultiSelection: selection.hasMultiSelection,
        expandedBox: selection.expandedBox,
        computedTable,
        table,
      });
      const isCellSelectionLocked = computeIsCellSelectionEditingLocked({
        isTableLocked,
        selectedCells,
        rowData: table.rowData,
      });
      const selectionForWarning = selection.hasMultiSelection ? selection.expandedBox : null;
      const editWhileTableLockedWarning = resolveEditWhileTableLockedWarning(editWhileTableLockedWarningProp);
      const getEditWhileCellLockedWarning = resolveGetEditWhileCellLockedWarning(
        getEditWhileCellLockedWarningProp
      );
      const lockedWarningMessage = computeLockedWarningMessage({
        editingIsLocked: isCellSelectionLocked,
        isTableLocked,
        editWhileTableLockedWarning,
        getEditWhileCellLockedWarning,
        selection: selectionForWarning,
      });
      const anchorSelectedCells = computeAnchorSelectedEvaluatedCells({
        hasMultiSelection: selection.hasMultiSelection,
        multiStartIndex,
        computedTable,
        table,
      });
      const isAnchorCellSelectionLocked = computeIsCellSelectionEditingLocked({
        isTableLocked,
        selectedCells: anchorSelectedCells,
        rowData: table.rowData,
      });
      const hasLockedCellInSelection = computeHasLockedCellInSelection({
        hasMultiSelection: selection.hasMultiSelection,
        expandedBox: selection.expandedBox,
        table,
      });
      const isCellSelectionLockedForUndoRedo = computeIsCellSelectionEditingLocked({
        isTableLocked,
        selectedCells,
        rowData: table.rowData,
        forUndoRedo: true,
      });
      const editWhileTableLockedWarningMessage = locked
        ? computeLockedWarningMessage({
            editingIsLocked: true,
            isTableLocked,
            editWhileTableLockedWarning,
            getEditWhileCellLockedWarning: null,
            selection: null,
          })
        : null;
      const lockedWarningMessageIfAnchorLocked = computeLockedWarningMessage({
        editingIsLocked: isAnchorCellSelectionLocked,
        isTableLocked,
        editWhileTableLockedWarning,
        getEditWhileCellLockedWarning,
        selection: selectionForWarning,
      });
      const lockedWarningMessageForUndoRedo = computeLockedWarningMessage({
        editingIsLocked: isCellSelectionLockedForUndoRedo,
        isTableLocked,
        editWhileTableLockedWarning,
        getEditWhileCellLockedWarning,
        selection: selectionForWarning,
      });
      return {
        isTableLocked,
        isCellSelectionLocked,
        isLockedWithEditWarn: isCellSelectionLocked,
        lockedWarningMessage,
        isAnchorCellSelectionLocked,
        isCellSelectionLockedForUndoRedo,
        hasLockedCellInSelection,
        editWhileTableLockedWarningMessage,
        lockedWarningMessageIfAnchorLocked,
        lockedWarningMessageForUndoRedo,
        lockedRowIndices,
      };
    }
  );
}
